import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import LumpSumAllocator, { AllocatorSnapshot } from './LumpSumAllocator';

interface FeeLineItem {
  fee_type_id: string;
  fee_name: string;
  amount: number;
}

interface BulkOptionalFee {
  fee_type_id: string;
  fee_name: string;
  byMonth: Record<string, number>; // month_year → amount
}

interface StudentDisplayInfo {
  student_name?: string;
  class?: string;
  section?: string | null;
  roll_no?: number;
}

interface LateFeeRaw {
  due_date: string;
  late_fee_type: 'per_day' | 'per_month' | 'percentage' | 'none';
  late_fee_amount: number;
  late_fee_max: number | null;
  grace_days: number;
}

interface LateFeeInfo extends LateFeeRaw {
  days_late: number;
  calculated_amount: number;
  is_late: boolean;
}

interface ExistingRecordItem {
  fee_type_id?: string | { $oid: string };
  fee_name: string;
  amount: number;
  is_optional: boolean;
  is_exempted: boolean;
}

interface ExistingFeeRecord {
  fee_record_id: number | string | null;
  pay_mode: string | null;
  paid_amount: string | number | null;
  total_amount?: string | number | null;
  is_exempted?: boolean;
  exemption_reason?: string | null;
  fee_items?: ExistingRecordItem[];
  late_fee_applied?: number;
  late_fee_days?: number;
  late_fee_exempted?: boolean;
  late_fee_waived?: number;
  is_settled?: boolean;
  settlement_reason?: string | null;
  refund_amount?: number;
  refund_status?: string | null;
}

interface MarkFeeModalProps {
  open: boolean;
  onClose: () => void;
  studentId: string | null;
  session: string | null;
  sessionId: string | null;
  /** Single month `YYYY-MM` or multiple months for bulk mark */
  monthYear: string | string[] | null;
  classId?: string | null;
  schoolId?: string;
  /** Session boundary months ("April" / "March") — needed to find pending months when a payment ends up over-collected. */
  sessionStart?: string | null;
  sessionEnd?: string | null;
  studentInfo?: StudentDisplayInfo | null;
  /** When set (single-month only), the modal opens in edit mode and rebuilds this record instead of creating a new one. */
  existingRecord?: ExistingFeeRecord | null;
  onSuccess: () => void;
  toast: React.RefObject<Toast | null>;
}

const PAY_MODE_OPTIONS = [
  { label: 'Cash', value: 'cash' },
  { label: 'Online', value: 'online' },
  { label: 'Cheque', value: 'cheque' },
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const fmtMoney = (n: number) =>
  `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const fmtMonthFull = (my: string) => {
  const [year, month] = my.split('-');
  const idx = parseInt(month, 10) - 1;
  return `${MONTH_NAMES[idx] ?? month} ${year}`;
};

const toId = (v: any): string => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (v.$oid) return v.$oid;
  return String(v);
};

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const daysBetween = (from: Date, to: Date) => {
  const ms = startOfDay(to).getTime() - startOfDay(from).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

const computeLateFee = (
  cfg: LateFeeRaw,
  baseForPercent: number,
  today = new Date()
): LateFeeInfo => {
  const due = new Date(cfg.due_date);
  const grace = cfg.grace_days ?? 0;
  const effectiveDue = new Date(due);
  effectiveDue.setDate(effectiveDue.getDate() + grace);

  const isPastDue = startOfDay(today) > startOfDay(effectiveDue);
  const daysLate = isPastDue ? daysBetween(effectiveDue, today) : 0;

  let calculated = 0;
  const type = cfg.late_fee_type || 'none';
  const amount = Number(cfg.late_fee_amount) || 0;
  const max = cfg.late_fee_max != null ? Number(cfg.late_fee_max) : null;

  if (isPastDue && type !== 'none') {
    if (type === 'per_day') calculated = amount * daysLate;
    else if (type === 'per_month') calculated = amount;
    else if (type === 'percentage') calculated = (baseForPercent * amount) / 100;
    if (max != null && max > 0 && calculated > max) calculated = max;
  }

  return {
    ...cfg,
    late_fee_type: type,
    days_late: daysLate,
    calculated_amount: Math.round(calculated * 100) / 100,
    is_late: isPastDue && calculated > 0,
  };
};

// Declared at module scope on purpose. Defining these inside the component
// would make them a new component type on every render, which remounts their
// children — the embedded LumpSumAllocator would refetch in a loop.
const SectionHeader = ({ icon, title }: { icon: string; title: string }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      marginBottom: 14,
      paddingBottom: 12,
      borderBottom: '1px solid rgba(229,231,235,0.6)',
    }}
  >
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
      }}
    >
      <i className={`pi ${icon}`} style={{ fontSize: 14 }} />
    </div>
    <span
      style={{
        color: '#1e3a8a',
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
      }}
    >
      {title}
    </span>
  </div>
);

const Card = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      background: 'rgba(255,255,255,0.8)',
      borderRadius: 16,
      padding: 20,
      border: '1px solid rgba(59,130,246,0.1)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    }}
  >
    {children}
  </div>
);

const MarkFeeModal: React.FC<MarkFeeModalProps> = ({
  open,
  onClose,
  studentId,
  session,
  sessionId,
  monthYear,
  classId,
  schoolId,
  sessionStart,
  sessionEnd,
  studentInfo,
  existingRecord,
  onSuccess,
  toast,
}) => {
  // Normalize months + bulk flag
  const months: string[] = useMemo(() => {
    if (!monthYear) return [];
    if (Array.isArray(monthYear)) return [...new Set(monthYear.filter(Boolean))].sort();
    return [monthYear];
  }, [monthYear]);

  const isBulk = months.length > 1;
  const primaryMonth = months[0] ?? null;
  const isEdit = !isBulk && !!existingRecord?.fee_record_id;

  const [mandatoryFees, setMandatoryFees] = useState<FeeLineItem[]>([]);
  const [optionalFees, setOptionalFees] = useState<FeeLineItem[]>([]);
  const [selectedOptionalIds, setSelectedOptionalIds] = useState<string[]>([]);
  const [payMode, setPayMode] = useState<string>('cash');
  const [paidAmount, setPaidAmount] = useState<string>('');
  const [isExempted, setIsExempted] = useState(false);
  const [exemptionReason, setExemptionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [lateFeeRaw, setLateFeeRaw] = useState<LateFeeRaw | null>(null);

  // Extra state for bulk
  const [includeLateFee, setIncludeLateFee] = useState(false);
  const [bulkOptionalFees, setBulkOptionalFees] = useState<BulkOptionalFee[]>([]);
  const [bulkMandatoryEstimate, setBulkMandatoryEstimate] = useState<number | null>(null);
  const [bulkLateByMonth, setBulkLateByMonth] = useState<Record<string, number>>({});
  const [bulkLateDaysByMonth, setBulkLateDaysByMonth] = useState<Record<string, number>>({});
  // Per-month optional fee selection for bulk mode: month_year -> [fee_type_id]
  const [bulkOptionalSelections, setBulkOptionalSelections] = useState<Record<string, string[]>>({});

  // One-time settlement (single month): a reduced amount accepted as full &
  // final. The month is marked paid and the shortfall stored as a discount.
  const [isSettlement, setIsSettlement] = useState(false);
  const [settlementReason, setSettlementReason] = useState('');

  // Overpayment (edit mode): the recorded payment now exceeds the charge —
  // typically a late fee that was collected and then removed. The money is
  // real, so the user has to say where it goes.
  const [overpaymentAction, setOverpaymentAction] = useState<'apply' | 'refund' | 'correct' | ''>('');
  const [refundNote, setRefundNote] = useState('');
  // Live state of the embedded allocator (pending months, optional fee
  // choices, late-fee decision, coverage preview) — what "apply" will send.
  const [applySnapshot, setApplySnapshot] = useState<AllocatorSnapshot | null>(null);
  const handleApplySnapshot = useCallback((s: AllocatorSnapshot) => setApplySnapshot(s), []);

  useEffect(() => {
    if (open && studentId && sessionId && months.length > 0) {
      loadFeeBreakdown();
    } else if (!open) {
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, studentId, sessionId, months.join(','), classId]);

  useEffect(() => {
    if (isClosing) {
      document.querySelector('.mark-fee-dialog')?.classList.add('dialog-closing');
      document.querySelector('.mark-fee-mask')?.classList.add('mask-closing');
    }
  }, [isClosing]);

  const resetState = () => {
    setMandatoryFees([]);
    setOptionalFees([]);
    setSelectedOptionalIds([]);
    setPayMode('cash');
    setPaidAmount('');
    setIsExempted(false);
    setExemptionReason('');
    setLateFeeRaw(null);
    setIncludeLateFee(false);
    setBulkOptionalFees([]);
    setBulkMandatoryEstimate(null);
    setBulkLateByMonth({});
    setBulkLateDaysByMonth({});
    setBulkOptionalSelections({});
    setIsSettlement(false);
    setSettlementReason('');
    setOverpaymentAction('');
    setRefundNote('');
    setApplySnapshot(null);
  };

  const loadFeeBreakdown = async () => {
    if (!studentId || !sessionId || !primaryMonth) return;
    setFetching(true);
    try {
      if (isBulk) {
        // ── Bulk mode ──
        // Get optional fees for each month
        const optFeeMap = new Map<string, BulkOptionalFee>();

        for (const my of months) {
          try {
            const optParams = new URLSearchParams({
              student_id: studentId,
              session_id: sessionId,
              month_year: my,
            });
            const optRes = await apiClient.get(
              `/api/fee-records/optional-active?${optParams.toString()}`
            );
            if (optRes.data.status === 'success') {
              for (const f of optRes.data.data || []) {
                const id = toId(f.fee_type_id);
                if (!id) continue;
                if (!optFeeMap.has(id)) {
                  optFeeMap.set(id, {
                    fee_type_id: id,
                    fee_name: f.fee_name || '—',
                    byMonth: {},
                  });
                }
                const existing = optFeeMap.get(id)!;
                existing.byMonth[my] = Number(f.amount) || 0;
              }
            }
          } catch {
            // ignore
          }
        }
        setBulkOptionalFees(Array.from(optFeeMap.values()));

        // Get mandatory estimate from primary month structure (skip optional types)
        let mandatoryList: FeeLineItem[] = [];
        if (classId) {
          const structParams = new URLSearchParams({
            session_id: sessionId,
            class_id: classId,
            month_year: primaryMonth,
            limit: '100',
          });
          if (schoolId) structParams.set('school_id', schoolId);
          const structRes = await apiClient.get(
            `/api/fee-structures?${structParams.toString()}`
          );
          if (structRes.data.status === 'success') {
            const optIds = new Set(optFeeMap.keys());
            for (const s of structRes.data.data || []) {
              const id = toId(s.fee_type_id);
              if (!id || optIds.has(id)) continue;
              mandatoryList.push({
                fee_type_id: id,
                fee_name: s.fee_name || '—',
                amount: Number(s.amount) || 0,
              });
            }
          }
        }
        setMandatoryFees(mandatoryList);
        setBulkMandatoryEstimate(
          mandatoryList.reduce((s, f) => s + f.amount, 0)
        );

        // Late config per month
        const lateMap: Record<string, number> = {};
        if (classId) {
          try {
            const lateParams = new URLSearchParams({
              session_id: sessionId,
              class_id: classId,
              limit: '50',
            });
            if (schoolId) lateParams.set('school_id', schoolId);
            const lateRes = await apiClient.get(
              `/api/fee-late-config?${lateParams.toString()}`
            );
            if (lateRes.data.status === 'success') {
              const configs = lateRes.data.data || [];
              const monthEntries: Record<string, any> = {};
              for (const cfg of configs) {
                for (const m of cfg.months || []) {
                  const my = m.month_year || m.MonthYear;
                  if (my) monthEntries[my] = m;
                }
              }
              const base = mandatoryList.reduce((s, f) => s + f.amount, 0);
              const daysMap: Record<string, number> = {};
              for (const my of months) {
                const entry = monthEntries[my];
                if (!entry) continue;
                const raw: LateFeeRaw = {
                  due_date: entry.due_date || entry.DueDate,
                  late_fee_type: (entry.late_fee_type || entry.LateFeeType || 'none') as LateFeeRaw['late_fee_type'],
                  late_fee_amount: Number(entry.late_fee_amount ?? entry.LateFeeAmount ?? 0),
                  late_fee_max:
                    entry.late_fee_max != null || entry.LateFeeMax != null
                      ? Number(entry.late_fee_max ?? entry.LateFeeMax)
                      : null,
                  grace_days: Number(entry.grace_days ?? entry.GraceDays ?? 0),
                };
                const info = computeLateFee(raw, base);
                if (info.is_late) {
                  lateMap[my] = info.calculated_amount;
                  daysMap[my] = info.days_late;
                }
              }
              setBulkLateDaysByMonth(daysMap);
            }
          } catch {
            /* ignore */
          }
        }
        setBulkLateByMonth(lateMap);
      } else {
        // ── Single month: full breakdown ──
        const optParams = new URLSearchParams({
          student_id: studentId,
          session_id: sessionId,
          month_year: primaryMonth,
        });
        const optRes = await apiClient.get(
          `/api/fee-records/optional-active?${optParams.toString()}`
        );

        const optionalList: FeeLineItem[] = [];
        const optionalIdSet = new Set<string>();
        if (optRes.data.status === 'success') {
          for (const f of optRes.data.data || []) {
            const id = toId(f.fee_type_id);
            if (!id) continue;
            optionalIdSet.add(id);
            optionalList.push({
              fee_type_id: id,
              fee_name: f.fee_name || '—',
              amount: f.amount != null ? Number(f.amount) : 0,
            });
          }
        }
        setOptionalFees(optionalList);

        let mandatoryList: FeeLineItem[] = [];
        if (classId) {
          const structParams = new URLSearchParams({
            session_id: sessionId,
            class_id: classId,
            month_year: primaryMonth,
            limit: '100',
          });
          if (schoolId) structParams.set('school_id', schoolId);
          const structRes = await apiClient.get(
            `/api/fee-structures?${structParams.toString()}`
          );
          if (structRes.data.status === 'success') {
            for (const s of structRes.data.data || []) {
              const id = toId(s.fee_type_id);
              if (!id || optionalIdSet.has(id)) continue;
              mandatoryList.push({
                fee_type_id: id,
                fee_name: s.fee_name || '—',
                amount: Number(s.amount) || 0,
              });
            }
          }
        }
        setMandatoryFees(mandatoryList);

        setLateFeeRaw(null);
        setIncludeLateFee(false);
        if (classId) {
          try {
            const lateParams = new URLSearchParams({
              session_id: sessionId,
              class_id: classId,
              month_year: primaryMonth,
              limit: '10',
            });
            if (schoolId) lateParams.set('school_id', schoolId);
            const lateRes = await apiClient.get(
              `/api/fee-late-config?${lateParams.toString()}`
            );
            if (lateRes.data.status === 'success') {
              for (const cfg of lateRes.data.data || []) {
                const entry = (cfg.months || []).find(
                  (m: any) => (m.month_year || m.MonthYear) === primaryMonth
                );
                if (entry) {
                  setLateFeeRaw({
                    due_date: entry.due_date || entry.DueDate,
                    late_fee_type: (entry.late_fee_type ||
                      entry.LateFeeType ||
                      'none') as LateFeeRaw['late_fee_type'],
                    late_fee_amount: Number(
                      entry.late_fee_amount ?? entry.LateFeeAmount ?? 0
                    ),
                    late_fee_max:
                      entry.late_fee_max != null || entry.LateFeeMax != null
                        ? Number(entry.late_fee_max ?? entry.LateFeeMax)
                        : null,
                    grace_days: Number(entry.grace_days ?? entry.GraceDays ?? 0),
                  });
                  break;
                }
              }
            }
          } catch {
            /* ignore */
          }
        }
      }
    } catch (err: any) {
      console.error('Failed to load fee breakdown', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to load fee details',
        life: 4000,
      });
    } finally {
      setFetching(false);
    }

    // ── Edit mode: overlay the existing record's own selections ──
    if (isEdit && existingRecord) {
      const optionalIds = (existingRecord.fee_items || [])
        .filter(i => i.is_optional)
        .map(i => toId(i.fee_type_id))
        .filter(Boolean);
      setSelectedOptionalIds(optionalIds);
      setPayMode(existingRecord.pay_mode || 'cash');
      setPaidAmount(
        existingRecord.paid_amount != null ? String(existingRecord.paid_amount) : ''
      );
      setIsExempted(!!existingRecord.is_exempted);
      setExemptionReason(existingRecord.exemption_reason || '');
      setIncludeLateFee((existingRecord.late_fee_applied || 0) > 0);
      setIsSettlement(!!existingRecord.is_settled);
      setSettlementReason(existingRecord.settlement_reason || '');
    }
  };

  const handleOptionalToggle = (feeTypeId: string) => {
    setSelectedOptionalIds((prev) =>
      prev.includes(feeTypeId)
        ? prev.filter((id) => id !== feeTypeId)
        : [...prev, feeTypeId]
    );
  };

  // Toggle a single optional fee for a single month in bulk mode
  const handleBulkOptionalToggle = (my: string, feeTypeId: string) => {
    setBulkOptionalSelections((prev) => {
      const current = prev[my] || [];
      const next = current.includes(feeTypeId)
        ? current.filter((id) => id !== feeTypeId)
        : [...current, feeTypeId];
      return { ...prev, [my]: next };
    });
  };

  // Select / clear an optional fee across every month it's available in
  const handleBulkOptionalToggleAll = (feeTypeId: string) => {
    const fee = bulkOptionalFees.find((f) => f.fee_type_id === feeTypeId);
    if (!fee) return;
    const eligibleMonths = months.filter((my) => fee.byMonth[my] != null);
    const allSelected = eligibleMonths.every((my) =>
      (bulkOptionalSelections[my] || []).includes(feeTypeId)
    );
    setBulkOptionalSelections((prev) => {
      const next = { ...prev };
      for (const my of eligibleMonths) {
        const current = next[my] || [];
        if (allSelected) {
          next[my] = current.filter((id) => id !== feeTypeId);
        } else if (!current.includes(feeTypeId)) {
          next[my] = [...current, feeTypeId];
        }
      }
      return next;
    });
  };

  const getMonthOptionalTotal = (my: string) => {
    const selected = bulkOptionalSelections[my] || [];
    return selected.reduce((s, id) => {
      const fee = bulkOptionalFees.find((f) => f.fee_type_id === id);
      return s + (fee?.byMonth[my] ?? 0);
    }, 0);
  };

  const mandatoryTotal = useMemo(
    () => mandatoryFees.reduce((sum, f) => sum + (f.amount || 0), 0),
    [mandatoryFees]
  );

  const optionalSelectedTotal = useMemo(
    () =>
      selectedOptionalIds.reduce((sum, id) => {
        const fee = optionalFees.find((f) => f.fee_type_id === id);
        return sum + (fee?.amount || 0);
      }, 0),
    [selectedOptionalIds, optionalFees]
  );

  const bulkOptionalSelectedTotal = useMemo(() => {
    if (!isBulk) return 0;
    return months.reduce((sum, my) => sum + getMonthOptionalTotal(my), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBulk, months, bulkOptionalSelections, bulkOptionalFees]);

  const bulkLateTotal = useMemo(() => {
    if (!isBulk || !includeLateFee) return 0;
    return months.reduce((s, my) => s + (bulkLateByMonth[my] || 0), 0);
  }, [isBulk, includeLateFee, months, bulkLateByMonth]);

  const bulkLateApplicableTotal = useMemo(
    () => Object.values(bulkLateByMonth).reduce((a, b) => a + b, 0),
    [bulkLateByMonth]
  );

  const effectiveLateFee = useMemo(() => {
    if (isBulk || !lateFeeRaw) return null;
    const base =
      lateFeeRaw.late_fee_type === 'percentage'
        ? mandatoryTotal + optionalSelectedTotal
        : mandatoryTotal;
    return computeLateFee(lateFeeRaw, base);
  }, [isBulk, lateFeeRaw, mandatoryTotal, optionalSelectedTotal]);

  const singleLateAmount =
    !isBulk && includeLateFee && effectiveLateFee?.is_late
      ? effectiveLateFee.calculated_amount
      : 0;

  const bulkEstimateTotal =
    isBulk && bulkMandatoryEstimate != null
      ? bulkMandatoryEstimate * months.length + bulkLateTotal + bulkOptionalSelectedTotal
      : null;

  const grandTotal = isExempted
    ? 0
    : isBulk
      ? bulkEstimateTotal ?? 0
      : mandatoryTotal + optionalSelectedTotal + singleLateAmount;

  const parsedPaid = parseFloat(paidAmount);

  // Money already collected now exceeds what's being charged.
  const overpaid =
    isEdit &&
    !isExempted &&
    !isBulk &&
    paidAmount.trim() !== '' &&
    !isNaN(parsedPaid) &&
    parsedPaid > grandTotal
      ? Math.round((parsedPaid - grandTotal) * 100) / 100
      : 0;

  const canSettle =
    !isBulk &&
    !isExempted &&
    overpaid === 0 &&
    paidAmount.trim() !== '' &&
    !isNaN(parsedPaid) &&
    parsedPaid > 0 &&
    parsedPaid < grandTotal;

  // Keep the settlement flag honest — if the amount stops being a shortfall,
  // it isn't a settlement any more.
  useEffect(() => {
    if (isSettlement && !canSettle) setIsSettlement(false);
  }, [isSettlement, canSettle]);

  // Default the overpayment decision to "apply" as soon as an excess appears;
  // clear it (and the embedded allocator) when the excess goes away.
  useEffect(() => {
    if (overpaid > 0) {
      if (overpaymentAction === '') setOverpaymentAction('apply');
    } else if (overpaymentAction !== '' || applySnapshot !== null) {
      setOverpaymentAction('');
      setApplySnapshot(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overpaid]);

  const applyLoading = overpaymentAction === 'apply' && (applySnapshot?.loading ?? true);
  const applyMonths = applySnapshot?.chargeableMonths ?? [];
  const applyCoverage = applySnapshot?.coverage ?? null;
  const leftoverAfterApply = overpaymentAction === 'apply' ? applyCoverage?.leftover ?? 0 : 0;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const postOneMonth = async (my: string, withLate: number, lateDays: number = 0) => {
    const body: Record<string, any> = {
      session_id: sessionId,
      session,
      student_id: studentId,
      month_year: my,
      pay_mode: isExempted ? 'cash' : payMode,
      is_exempted: isExempted,
    };
    if (schoolId) body.school_id = schoolId;
    if (isExempted && exemptionReason.trim()) {
      body.exemption_reason = exemptionReason.trim();
    }

    if (isBulk) {
      const selected = bulkOptionalSelections[my] || [];
      if (selected.length > 0) {
        body.optional_fees = selected
          .map((id) => {
            const fee = bulkOptionalFees.find((f) => f.fee_type_id === id);
            const amount = fee?.byMonth[my];
            if (amount == null) return null;
            return { fee_type_id: id, amount };
          })
          .filter(Boolean);
      }
    } else if (selectedOptionalIds.length > 0) {
      body.optional_fees = selectedOptionalIds.map((id) => {
        const fee = optionalFees.find((f) => f.fee_type_id === id);
        return { fee_type_id: id, amount: fee?.amount ?? 0 };
      });
    }

    if (withLate > 0) {
      body.late_fee_amount = withLate;
      body.late_fee_days = lateDays;
    } else if (!isExempted) {
      // A late fee applied to this month but wasn't charged — record the
      // waiver so the decision is visible on the receipt and in reports.
      const applicable = isBulk
        ? bulkLateByMonth[my] || 0
        : effectiveLateFee?.is_late
          ? effectiveLateFee.calculated_amount
          : 0;
      if (applicable > 0) {
        body.late_fee_exempted = true;
        body.late_fee_waived_amount = applicable;
      }
    }

    if (!isBulk && isSettlement && !isExempted) {
      body.is_settlement = true;
      if (settlementReason.trim()) body.settlement_reason = settlementReason.trim();
    }

    if (overpaid > 0) {
      body.overpayment_action = overpaymentAction;
      if (refundNote.trim()) body.refund_note = refundNote.trim();
      if (overpaymentAction === 'apply' && applySnapshot) {
        // Same shape the lump-sum endpoint takes — the backend routes it into
        // the shared allocator with this month excluded.
        body.overpayment_apply = {
          optional_fee_selections: applySnapshot.optionalFeeSelections,
          include_late_fee: applySnapshot.includeLateFee,
          monthly_late_fees: applySnapshot.monthlyLateFees,
          monthly_late_days: applySnapshot.monthlyLateDays,
        };
      }
    }

    if (!isExempted) {
      if (!isBulk && paidAmount.trim() !== '') {
        const n = parseFloat(paidAmount);
        if (!isNaN(n) && n >= 0) body.paid_amount = n;
      }
      // Otherwise leave paid_amount unset — the backend defaults it to the
      // full total_amount, which now includes late_fee_amount server-side.
    }

    if (isEdit && existingRecord?.fee_record_id) {
      return apiClient.put(`/api/fee-records/${existingRecord.fee_record_id}`, body);
    }
    return apiClient.post('/api/fee-records', body);
  };

  const handleSubmit = async () => {
    if (!studentId || !sessionId || !session || months.length === 0) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Missing student, session or month',
        life: 4000,
      });
      return;
    }
    if (!isExempted && !payMode) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Payment mode',
        detail: 'Please select a payment mode',
        life: 3000,
      });
      return;
    }
    if (overpaid > 0 && !overpaymentAction) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Extra amount',
        detail: `${fmtMoney(overpaid)} was already collected. Choose what happens to it before saving.`,
        life: 4000,
      });
      return;
    }

    setLoading(true);
    let ok = 0;
    let fail = 0;
    try {
      if (isBulk) {
        for (const my of months) {
          try {
            const late = includeLateFee ? bulkLateByMonth[my] || 0 : 0;
            const lateDays = includeLateFee ? bulkLateDaysByMonth[my] || 0 : 0;
            const res = await postOneMonth(my, late, lateDays);
            if (res.data.status === 'success') ok++;
            else fail++;
          } catch {
            fail++;
          }
        }
        if (ok > 0) {
          toast.current?.show({
            severity: fail ? 'warn' : 'success',
            summary: fail ? 'Partial success' : 'Success',
            detail: `${ok} month(s) marked paid${fail ? `, ${fail} failed` : ''}.`,
            life: 4000,
          });
          setTimeout(() => {
            onSuccess();
            handleClose();
          }, 300);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Could not mark any months as paid',
            life: 5000,
          });
        }
      } else {
        const singleLateDays =
          includeLateFee && effectiveLateFee?.is_late ? effectiveLateFee.days_late : 0;
        const res = await postOneMonth(primaryMonth!, singleLateAmount, singleLateDays);
        if (res.data.status === 'success') {
          // Spell out what happened to any excess — that's the part the user
          // most needs confirmed.
          const applied: any[] = res.data.applied_months || [];
          const refunded = Number(res.data.refund_amount) || 0;
          let detail: string =
            res.data.message ||
            (isSettlement
              ? 'Settlement recorded — month marked paid.'
              : 'Fee marked as paid successfully!');
          if (applied.length > 0) {
            const names = applied.map((m: any) => fmtMonthFull(m.month_year));
            const span =
              names.length === 1 ? names[0] : `${names[0]} – ${names[names.length - 1]}`;
            detail = `Updated. ${fmtMoney(overpaid)} applied to ${applied.length} month${
              applied.length > 1 ? 's' : ''
            } (${span})${refunded > 0 ? `; ${fmtMoney(refunded)} pending refund.` : '.'}`;
          } else if (refunded > 0) {
            detail = `Updated. ${fmtMoney(refunded)} logged as a pending refund.`;
          }
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail,
            life: 5000,
          });
          setTimeout(() => {
            onSuccess();
            handleClose();
          }, 300);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: res.data.message || 'Failed to mark fee as paid',
            life: 5000,
          });
        }
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to mark fee as paid',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const studentName = studentInfo?.student_name || 'Student';
  const classLabel = studentInfo?.class
    ? `${studentInfo.class}${
        studentInfo.section ? ` · Sec ${String(studentInfo.section).toUpperCase()}` : ''
      }${studentInfo.roll_no != null ? ` · Roll ${studentInfo.roll_no}` : ''}`
    : null;

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
      <div
        style={{
          width: 54,
          height: 54,
          borderRadius: 14,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: 22,
          boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
          flexShrink: 0,
          border: '3px solid white',
        }}
      >
        <i className="pi pi-indian-rupee" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: 4 }}>
          {isBulk ? `Mark ${months.length} months` : isEdit ? 'Edit Fee Record' : 'Mark Fee Payment'}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {studentName}
          {!isBulk && primaryMonth ? ` — ${primaryMonth}` : ''}
        </div>
      </div>
    </div>
  );

  const hasBulkLate = Object.keys(bulkLateByMonth).length > 0;

  const submitLabel = loading
    ? 'Processing...'
    : isBulk
      ? `Mark ${months.length} months paid`
      : isExempted
        ? isEdit ? 'Update as Exempted' : 'Mark Exempted'
        : overpaid > 0
          ? overpaymentAction === 'apply'
            ? 'Update & Apply to Pending Months'
            : overpaymentAction === 'refund'
              ? 'Update & Log Refund'
              : 'Update Fee Record'
          : isSettlement
            ? isEdit ? 'Update Settlement' : 'Settle & Mark Paid'
            : isEdit ? 'Update Fee Record' : 'Mark as Paid';

  return (
    <>
      <style>{`
        .mark-fee-dialog-mask {
          backdrop-filter: blur(4px);
          background-color: rgba(0, 0, 0, 0.25) !important;
          animation: mfMaskIn 0.35s ease;
        }
        .mark-fee-dialog-mask.mask-closing { animation: mfMaskOut 0.4s ease !important; }
        @keyframes mfMaskIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes mfMaskOut { from { opacity: 1; } to { opacity: 0; } }
        .mark-fee-dialog {
          animation: mfDialogIn 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
        }
        .mark-fee-dialog.dialog-closing {
          animation: mfDialogOut 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        @keyframes mfDialogIn {
          from { opacity: 0; transform: scale(0.92) translateY(-20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes mfDialogOut {
          from { opacity: 1; transform: scale(1) translateY(0); }
          to   { opacity: 0; transform: scale(0.88) translateY(25px); }
        }
        .optional-fee-row {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 14px; border-radius: 10px;
          background: rgba(59, 130, 246, 0.04);
          border: 1px solid rgba(59, 130, 246, 0.08);
          cursor: pointer; transition: all 0.2s; user-select: none;
        }
        .optional-fee-row:hover {
          background: rgba(59, 130, 246, 0.09);
          border-color: rgba(59, 130, 246, 0.2);
        }
        .optional-fee-row.selected {
          background: rgba(59, 130, 246, 0.1);
          border-color: rgba(59, 130, 246, 0.35);
        }
        .optional-fee-checkbox {
          width: 18px; height: 18px; border-radius: 5px;
          border: 2px solid #94a3b8;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; transition: all 0.2s; background: white;
        }
        .optional-fee-row.selected .optional-fee-checkbox {
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
          border-color: transparent;
        }
        .fee-line {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 14px; border-radius: 10px;
          background: rgba(248,250,252,0.9);
          border: 1px solid rgba(226,232,240,0.8);
        }
        .month-optional-block {
          border: 1px solid rgba(226,232,240,0.8);
          border-radius: 12px;
          padding: 12px;
          background: rgba(248,250,252,0.6);
        }
        .month-optional-header {
          display: flex; align-items: center; justify-content: space-between;
        }
        .month-select-all {
          font-size: 11px; font-weight: 700; color: #3b82f6;
          cursor: pointer; user-select: none; text-decoration: underline;
          text-underline-offset: 2px;
        }
        .shimmer {
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
          background-size: 200% 100%;
          animation: shimmer 1.4s infinite;
          border-radius: 8px;
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .late-waived { color: #94a3b8; text-decoration: line-through; }
        .overpay-option {
          display: flex; align-items: flex-start; gap: 10px;
          padding: 10px 12px; border-radius: 10px;
          border: 1px solid rgba(253,230,138,0.6);
          background: transparent; transition: background 0.15s, border-color 0.15s;
        }
        .overpay-option.selected { background: white; border-color: #f59e0b; }
        .overpay-month {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 9px; border-radius: 8px;
          font-size: 11px; font-weight: 600;
          border: 1px solid #e2e8f0; color: #94a3b8; background: white;
        }
        .overpay-month.covered {
          border-color: #a7f3d0; color: #047857; background: rgba(16,185,129,0.08);
        }
        .set-total-link {
          font-size: 11px; font-weight: 700; color: #3b82f6;
          cursor: pointer; text-decoration: underline; text-underline-offset: 2px;
          background: none; border: none; padding: 0; font-family: inherit;
        }
      `}</style>

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        className="mark-fee-dialog"
        maskClassName="mark-fee-dialog-mask"
        style={{ width: '90vw', maxWidth: 860, borderRadius: 24, overflow: 'hidden' }}
        contentStyle={{
          padding: 18,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255,255,255,0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255,255,255,0.8)',
          borderRight: '12px solid rgba(255,255,255,0.8)',
          borderTop: '12px solid rgba(255,255,255,0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <SectionHeader icon="pi-user" title="Student" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { label: 'Name', value: studentName },
                { label: 'Class', value: classLabel },
                {
                  label: isBulk ? 'Months' : 'Month',
                  value: isBulk ? `${months.length} selected` : primaryMonth,
                },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderRadius: 8,
                    background: 'rgba(248,250,252,0.8)',
                    border: '1px solid rgba(226,232,240,0.6)',
                  }}
                >
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, minWidth: 56 }}>
                    {label}
                  </span>
                  <span style={{ fontSize: 13, color: '#1e3a8a', fontWeight: 600 }}>
                    {value || 'N/A'}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* Bulk: merged "months + per-month optional fees" card so each optional
              fee can be individually checked/unchecked per month. The late fee for
              each month is shown on its own row, struck through when waived. */}
          {isBulk && (
            <Card>
              <SectionHeader icon="pi-calendar" title="Months & optional fees" />
              {fetching ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div className="shimmer" style={{ height: 48 }} />
                  <div className="shimmer" style={{ height: 48 }} />
                </div>
              ) : (
                <div
                  style={{
                    maxHeight: 360,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  {months.map((my) => {
                    const applicableLate = bulkLateByMonth[my] || 0;
                    const monthOptFees = bulkOptionalFees.filter(
                      (f) => f.byMonth[my] != null
                    );
                    const monthSelected = bulkOptionalSelections[my] || [];

                    return (
                      <div key={my} className="month-optional-block">
                        <div
                          className="month-optional-header"
                          style={{ marginBottom: monthOptFees.length ? 10 : 0 }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a' }}>
                            {fmtMonthFull(my)}
                          </span>
                          {applicableLate > 0 ? (
                            <span
                              className={includeLateFee ? '' : 'late-waived'}
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: includeLateFee ? '#b91c1c' : undefined,
                              }}
                            >
                              +{fmtMoney(applicableLate)} late
                              {bulkLateDaysByMonth[my] ? ` · ${bulkLateDaysByMonth[my]}d` : ''}
                            </span>
                          ) : (
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>
                              Mandatory {fmtMoney(bulkMandatoryEstimate ?? 0)}
                            </span>
                          )}
                        </div>

                        {monthOptFees.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {monthOptFees.map((fee) => {
                              const selected = monthSelected.includes(fee.fee_type_id);
                              const amount = fee.byMonth[my] ?? 0;
                              return (
                                <div
                                  key={fee.fee_type_id}
                                  className={`optional-fee-row${selected ? ' selected' : ''}`}
                                  onClick={() =>
                                    !loading &&
                                    !isExempted &&
                                    handleBulkOptionalToggle(my, fee.fee_type_id)
                                  }
                                >
                                  <div className="optional-fee-checkbox">
                                    {selected && (
                                      <i
                                        className="pi pi-check"
                                        style={{ fontSize: 10, color: 'white' }}
                                      />
                                    )}
                                  </div>
                                  <span
                                    style={{
                                      fontSize: 13,
                                      color: '#1e3a8a',
                                      fontWeight: 500,
                                      flex: 1,
                                    }}
                                  >
                                    {fee.fee_name}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 700,
                                      color: '#1e40af',
                                      background: 'rgba(59,130,246,0.08)',
                                      padding: '2px 10px',
                                      borderRadius: 20,
                                      border: '1px solid rgba(59,130,246,0.15)',
                                    }}
                                  >
                                    {fmtMoney(amount)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Quick per-fee "apply to all months" toggles */}
              {bulkOptionalFees.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: '1px solid rgba(229,231,235,0.6)',
                  }}
                >
                  {bulkOptionalFees.map((fee) => {
                    const eligibleMonths = months.filter((my) => fee.byMonth[my] != null);
                    const allSelected =
                      eligibleMonths.length > 0 &&
                      eligibleMonths.every((my) =>
                        (bulkOptionalSelections[my] || []).includes(fee.fee_type_id)
                      );
                    return (
                      <span
                        key={fee.fee_type_id}
                        className="month-select-all"
                        onClick={() =>
                          !loading &&
                          !isExempted &&
                          handleBulkOptionalToggleAll(fee.fee_type_id)
                        }
                      >
                        {allSelected ? `Clear ${fee.fee_name}` : `Select ${fee.fee_name} for all`}
                      </span>
                    );
                  })}
                </div>
              )}

              {bulkOptionalFees.length === 0 && !fetching && (
                <div
                  style={{
                    padding: 14,
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: 13,
                    background: 'rgba(248,250,252,0.8)',
                    borderRadius: 10,
                    border: '1px dashed #e2e8f0',
                    marginTop: 10,
                  }}
                >
                  <i className="pi pi-info-circle" style={{ marginRight: 6 }} />
                  No optional fees available for these months
                </div>
              )}

              {bulkOptionalSelectedTotal > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    marginTop: 10,
                    borderRadius: 10,
                    background: 'rgba(59,130,246,0.08)',
                    border: '1px solid rgba(59,130,246,0.2)',
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>
                    Optional subtotal (all months)
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: '#1d4ed8' }}>
                    {fmtMoney(bulkOptionalSelectedTotal)}
                  </span>
                </div>
              )}
            </Card>
          )}

          {/* Bulk: mandatory fees (per month estimate) */}
          {isBulk && mandatoryFees.length > 0 && (
            <Card>
              <SectionHeader icon="pi-lock" title="Mandatory (per month estimate)" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {mandatoryFees.map((fee) => (
                  <div key={fee.fee_type_id} className="fee-line">
                    <span style={{ fontSize: 13, color: '#1e3a8a', fontWeight: 500 }}>
                      {fee.fee_name}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>
                      {fmtMoney(fee.amount)}
                    </span>
                  </div>
                ))}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderRadius: 10,
                    background: 'rgba(16,185,129,0.08)',
                    border: '1px solid rgba(16,185,129,0.2)',
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#047857' }}>
                    × {months.length} months
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: '#047857' }}>
                    {fmtMoney((bulkMandatoryEstimate ?? 0) * months.length)}
                  </span>
                </div>
              </div>
            </Card>
          )}

          {/* Single: mandatory */}
          {!isBulk && (
            <Card>
              <SectionHeader icon="pi-lock" title="Mandatory fees" />
              {fetching ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div className="shimmer" style={{ height: 40 }} />
                  <div className="shimmer" style={{ height: 40 }} />
                </div>
              ) : !classId ? (
                <div
                  style={{
                    padding: 14,
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: 13,
                    background: 'rgba(248,250,252,0.8)',
                    borderRadius: 10,
                    border: '1px dashed #e2e8f0',
                  }}
                >
                  Class is required to load mandatory fee structure.
                </div>
              ) : mandatoryFees.length === 0 ? (
                <div
                  style={{
                    padding: 14,
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: 13,
                    background: 'rgba(248,250,252,0.8)',
                    borderRadius: 10,
                    border: '1px dashed #e2e8f0',
                  }}
                >
                  No mandatory fee structure for this class / month.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {mandatoryFees.map((fee) => (
                    <div key={fee.fee_type_id} className="fee-line">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <i className="pi pi-check-circle" style={{ fontSize: 12, color: '#059669' }} />
                        <span style={{ fontSize: 13, color: '#1e3a8a', fontWeight: 500 }}>
                          {fee.fee_name}
                        </span>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>
                        {fmtMoney(fee.amount)}
                      </span>
                    </div>
                  ))}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      marginTop: 4,
                      borderRadius: 10,
                      background: 'rgba(16,185,129,0.08)',
                      border: '1px solid rgba(16,185,129,0.2)',
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#047857' }}>
                      Mandatory subtotal
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 800, color: '#047857' }}>
                      {fmtMoney(mandatoryTotal)}
                    </span>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Single: optional */}
          {!isBulk && (
            <Card>
              <SectionHeader icon="pi-list" title="Optional fees" />
              {fetching ? (
                <div className="shimmer" style={{ height: 48 }} />
              ) : optionalFees.length === 0 ? (
                <div
                  style={{
                    padding: 14,
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: 13,
                    background: 'rgba(248,250,252,0.8)',
                    borderRadius: 10,
                    border: '1px dashed #e2e8f0',
                  }}
                >
                  <i className="pi pi-info-circle" style={{ marginRight: 6 }} />
                  No optional fees available
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {optionalFees.map((fee) => {
                    const selected = selectedOptionalIds.includes(fee.fee_type_id);
                    return (
                      <div
                        key={fee.fee_type_id}
                        className={`optional-fee-row${selected ? ' selected' : ''}`}
                        onClick={() =>
                          !loading && !isExempted && handleOptionalToggle(fee.fee_type_id)
                        }
                      >
                        <div className="optional-fee-checkbox">
                          {selected && (
                            <i className="pi pi-check" style={{ fontSize: 10, color: 'white' }} />
                          )}
                        </div>
                        <span style={{ fontSize: 13, color: '#1e3a8a', fontWeight: 500, flex: 1 }}>
                          {fee.fee_name}
                        </span>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#1e40af',
                            background: 'rgba(59,130,246,0.08)',
                            padding: '2px 10px',
                            borderRadius: 20,
                            border: '1px solid rgba(59,130,246,0.15)',
                          }}
                        >
                          {fmtMoney(fee.amount)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}

          {/* Late fee (optional include) — single or bulk */}
          {((!isBulk &&
            effectiveLateFee &&
            effectiveLateFee.late_fee_type !== 'none') ||
            (isBulk && hasBulkLate)) && (
            <Card>
              <SectionHeader icon="pi-clock" title="Late fee (optional)" />
              {!isBulk && effectiveLateFee && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div className="fee-line">
                    <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Due date</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a' }}>
                      {new Date(effectiveLateFee.due_date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                      {effectiveLateFee.grace_days > 0 && (
                        <span style={{ fontWeight: 500, color: '#94a3b8', marginLeft: 6 }}>
                          (+{effectiveLateFee.grace_days}d grace)
                        </span>
                      )}
                    </span>
                  </div>
                  {effectiveLateFee.is_late ? (
                    <>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '12px 14px',
                          borderRadius: 10,
                          cursor: loading || isExempted ? 'not-allowed' : 'pointer',
                          background: includeLateFee
                            ? 'rgba(239,68,68,0.1)'
                            : 'rgba(248,250,252,0.9)',
                          border: includeLateFee
                            ? '1px solid rgba(239,68,68,0.35)'
                            : '1px solid rgba(226,232,240,0.8)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={includeLateFee}
                          disabled={loading || isExempted}
                          onChange={(e) => setIncludeLateFee(e.target.checked)}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a' }}>
                            Include late fee ({effectiveLateFee.days_late}d late)
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>
                            Unchecked = skip (default)
                          </div>
                        </div>
                        <span
                          className={includeLateFee ? '' : 'late-waived'}
                          style={{ fontSize: 14, fontWeight: 800, color: includeLateFee ? '#b91c1c' : undefined }}
                        >
                          {fmtMoney(effectiveLateFee.calculated_amount)}
                        </span>
                      </label>
                      {!includeLateFee && !isExempted && (
                        <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>
                          This month will be saved with late_fee_exempted and{' '}
                          {fmtMoney(effectiveLateFee.calculated_amount)} recorded as waived.
                        </div>
                      )}
                    </>
                  ) : (
                    <div
                      style={{
                        padding: '10px 14px',
                        borderRadius: 10,
                        background: 'rgba(16,185,129,0.08)',
                        color: '#047857',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      On time — no late fee
                    </div>
                  )}
                </div>
              )}
              {isBulk && hasBulkLate && (
                <>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '12px 14px',
                      borderRadius: 10,
                      cursor: loading || isExempted ? 'not-allowed' : 'pointer',
                      background: includeLateFee
                        ? 'rgba(239,68,68,0.1)'
                        : 'rgba(248,250,252,0.9)',
                      border: includeLateFee
                        ? '1px solid rgba(239,68,68,0.35)'
                        : '1px solid rgba(226,232,240,0.8)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={includeLateFee}
                      disabled={loading || isExempted}
                      onChange={(e) => setIncludeLateFee(e.target.checked)}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a' }}>
                        Include late fees where applicable
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {Object.keys(bulkLateByMonth).length} of {months.length} month(s) overdue ·
                        skip by default
                      </div>
                    </div>
                    <span
                      className={includeLateFee ? '' : 'late-waived'}
                      style={{ fontSize: 14, fontWeight: 800, color: includeLateFee ? '#b91c1c' : undefined }}
                    >
                      {fmtMoney(bulkLateApplicableTotal)}
                    </span>
                  </label>
                  {!includeLateFee && !isExempted && (
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 8, lineHeight: 1.4 }}>
                      Each overdue month is saved with late_fee_exempted and its waived amount on record.
                    </div>
                  )}
                </>
              )}
            </Card>
          )}

          {/* Total */}
          <div
            style={{
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              borderRadius: 14,
              padding: '16px 20px',
            }}
          >
            {isBulk ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
                    Est. mandatory × {months.length}
                  </span>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                    {bulkMandatoryEstimate != null
                      ? fmtMoney(bulkMandatoryEstimate * months.length)
                      : '—'}
                  </span>
                </div>
                {bulkOptionalSelectedTotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
                      Optional fees
                    </span>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                      {fmtMoney(bulkOptionalSelectedTotal)}
                    </span>
                  </div>
                )}
                {bulkLateTotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: '#fecaca', fontWeight: 600 }}>Late fees</span>
                    <span style={{ fontSize: 13, color: '#fecaca', fontWeight: 700 }}>
                      {fmtMoney(bulkLateTotal)}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
                    Mandatory
                  </span>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                    {fmtMoney(mandatoryTotal)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
                    Optional
                  </span>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                    {fmtMoney(optionalSelectedTotal)}
                  </span>
                </div>
                {singleLateAmount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: '#fecaca', fontWeight: 600 }}>Late fee</span>
                    <span style={{ fontSize: 13, color: '#fecaca', fontWeight: 700 }}>
                      {fmtMoney(singleLateAmount)}
                    </span>
                  </div>
                )}
                {isSettlement && canSettle && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: '#a7f3d0', fontWeight: 600 }}>
                      Settlement discount
                    </span>
                    <span style={{ fontSize: 13, color: '#a7f3d0', fontWeight: 700 }}>
                      {fmtMoney(grandTotal - parsedPaid)}
                    </span>
                  </div>
                )}
                {overpaid > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: '#fde68a', fontWeight: 600 }}>
                      Collected over the charge
                    </span>
                    <span style={{ fontSize: 13, color: '#fde68a', fontWeight: 700 }}>
                      {fmtMoney(overpaid)}
                    </span>
                  </div>
                )}
              </>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 10,
                borderTop: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>
                {isBulk ? 'Est. grand total' : 'Grand total'}
              </span>
              <span style={{ fontSize: 22, fontWeight: 800, color: 'white' }}>
                {fmtMoney(grandTotal)}
              </span>
            </div>
          </div>

          {!isExempted && (
            <Card>
              <SectionHeader icon="pi-credit-card" title="Payment mode" />
              <Dropdown
                value={payMode}
                options={PAY_MODE_OPTIONS}
                onChange={(e) => setPayMode(e.value)}
                placeholder="Select mode"
                style={{ width: '100%' }}
                disabled={loading}
              />
            </Card>
          )}

          <Card>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
                marginBottom: isExempted ? 12 : 0,
              }}
            >
              <input
                type="checkbox"
                checked={isExempted}
                onChange={(e) => {
                  setIsExempted(e.target.checked);
                  if (e.target.checked) {
                    setIncludeLateFee(false);
                    setIsSettlement(false);
                  }
                }}
                disabled={loading}
              />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e3a8a' }}>
                Mark as exempted
              </span>
            </label>
            {isExempted && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '10px 14px',
                  marginBottom: 12,
                  borderRadius: 10,
                  background: '#fef9c3',
                  border: '1px solid #fde68a',
                }}
              >
                <i className="pi pi-info-circle" style={{ fontSize: 13, color: '#92400e', marginTop: 1 }} />
                <span style={{ fontSize: 12, color: '#92400e', fontWeight: 500, lineHeight: 1.4 }}>
                  {isBulk
                    ? `${months.length} month(s) will be marked exempted. No amount will be charged.`
                    : 'This month will be marked exempted. No amount will be charged.'}
                </span>
              </div>
            )}
            {isExempted && !isBulk && (
              <input
                type="text"
                placeholder="Exemption reason"
                value={exemptionReason}
                onChange={(e) => setExemptionReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid #e2e8f0',
                  fontSize: 13,
                }}
              />
            )}
            {!isExempted && !isBulk && (
              <div style={{ marginTop: 12 }}>
                {isEdit && existingRecord && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      borderRadius: 8,
                      background: '#f0f9ff',
                      border: '1px solid #bae6fd',
                      marginBottom: 10,
                    }}
                  >
                    <span style={{ fontSize: 11, color: '#0369a1', fontWeight: 600 }}>
                      Previously paid
                    </span>
                    <span style={{ fontSize: 13, color: '#0369a1', fontWeight: 700 }}>
                      {fmtMoney(Number(existingRecord.paid_amount) || 0)}
                      {existingRecord.total_amount != null
                        ? ` of ${fmtMoney(Number(existingRecord.total_amount) || 0)}`
                        : ''}
                    </span>
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 10,
                    marginBottom: 6,
                  }}
                >
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>
                    Paid amount (leave empty for full {fmtMoney(grandTotal)})
                  </label>
                  {overpaid > 0 && (
                    <button
                      type="button"
                      className="set-total-link"
                      onClick={() => setPaidAmount(String(grandTotal))}
                      disabled={loading}
                    >
                      Set to {fmtMoney(grandTotal)}
                    </button>
                  )}
                </div>

                <input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="Optional partial amount"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${overpaid > 0 ? '#fbbf24' : '#e2e8f0'}`,
                    fontSize: 13,
                  }}
                />

                {/* ── Overpayment: where does the extra money go? ── */}
                {overpaid > 0 && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: '12px 14px',
                      borderRadius: 10,
                      background: '#fffbeb',
                      border: '1px solid #fde68a',
                    }}
                  >
                    <div style={{ fontSize: 12, color: '#92400e', fontWeight: 700, lineHeight: 1.5 }}>
                      {fmtMoney(parsedPaid)} was collected for this month, but the charge is now{' '}
                      {fmtMoney(grandTotal)}.
                      {(existingRecord?.late_fee_applied ?? 0) > 0 && !includeLateFee
                        ? ` The ${fmtMoney(
                            existingRecord!.late_fee_applied!
                          )} late fee you removed was part of that payment.`
                        : ''}{' '}
                      Where does the extra <strong>{fmtMoney(overpaid)}</strong> go?
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                      {/* Option 1 — apply to pending months (full lump-sum controls embedded) */}
                      <label
                        className={`overpay-option${overpaymentAction === 'apply' ? ' selected' : ''}`}
                        style={{ cursor: 'pointer' }}
                      >
                        <input
                          type="radio"
                          name="overpayment"
                          checked={overpaymentAction === 'apply'}
                          onChange={() => setOverpaymentAction('apply')}
                          disabled={loading}
                          style={{ marginTop: 2 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>
                            Apply it to this student's pending months
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            Works exactly like Pay Lump Sum — pick optional fees, decide on late fees, exempt months
                            — then the {fmtMoney(overpaid)} is allocated oldest month first.
                          </div>
                        </div>
                      </label>

                      {overpaymentAction === 'apply' && (
                        <div style={{ paddingLeft: 4 }}>
                          <LumpSumAllocator
                            studentId={studentId}
                            sessionId={sessionId}
                            session={session}
                            sessionStart={sessionStart}
                            sessionEnd={sessionEnd}
                            classId={classId}
                            schoolId={schoolId}
                            pool={overpaid}
                            excludeMonths={primaryMonth ? [primaryMonth] : []}
                            disabled={loading}
                            compact
                            tableTitle={`Where the ${fmtMoney(overpaid)} goes — Month-wise`}
                            toast={toast}
                            onSnapshot={handleApplySnapshot}
                            onDataChanged={onSuccess}
                          />

                          {!applyLoading && applyCoverage && applyMonths.length > 0 && (
                            <div
                              style={{
                                marginTop: 10,
                                padding: '10px 14px',
                                borderRadius: 10,
                                background: applyCoverage.leftover > 0 ? '#fffbeb' : '#f0fdf4',
                                border: `1px solid ${applyCoverage.leftover > 0 ? '#fde68a' : '#bbf7d0'}`,
                                fontSize: 12,
                                color: applyCoverage.leftover > 0 ? '#92400e' : '#166534',
                                fontWeight: 600,
                                lineHeight: 1.5,
                              }}
                            >
                              {applyCoverage.fullMonths.length > 0 && (
                                <>
                                  Covers <strong>{applyCoverage.fullMonths.length}</strong> month
                                  {applyCoverage.fullMonths.length > 1 ? 's' : ''} in full
                                  {applyCoverage.partial ? ` and part of ${fmtMonthFull(applyCoverage.partial.month)}` : ''}.
                                </>
                              )}
                              {applyCoverage.fullMonths.length === 0 && applyCoverage.partial && (
                                <>Covers part of {fmtMonthFull(applyCoverage.partial.month)} ({fmtMoney(applyCoverage.partial.paid)} of {fmtMoney(applyCoverage.partial.total)}).</>
                              )}
                              {applyCoverage.leftover > 0 && (
                                <>
                                  {' '}<strong>{fmtMoney(applyCoverage.leftover)}</strong> is left over and will be logged
                                  as a pending refund on this record.
                                </>
                              )}
                              {applyCoverage.leftover === 0 && <> Nothing to refund.</>}
                            </div>
                          )}

                          {!applyLoading && applyMonths.length === 0 && (
                            <div
                              style={{
                                marginTop: 10, padding: '10px 14px', borderRadius: 10,
                                background: '#fffbeb', border: '1px solid #fde68a',
                                fontSize: 12, color: '#92400e', fontWeight: 600, lineHeight: 1.5,
                              }}
                            >
                              No pending months to apply this to — the whole {fmtMoney(overpaid)} will be logged as a
                              pending refund. Pick "Refund" below if that's the intent, or "Correct" if it was never
                              collected.
                            </div>
                          )}
                        </div>
                      )}

                      {/* Options 2 & 3 */}
                      {(
                        [
                          {
                            value: 'refund',
                            title: `Refund the full ${fmtMoney(overpaid)}`,
                            desc: 'Skip pending months. A pending refund is logged against this record.',
                          },
                          {
                            value: 'correct',
                            title: 'It was never collected — just correct the record',
                            desc: 'The late fee was ticked by mistake. Nothing is owed to anyone.',
                          },
                        ] as const
                      ).map(opt => (
                        <label
                          key={opt.value}
                          className={`overpay-option${overpaymentAction === opt.value ? ' selected' : ''}`}
                          style={{ cursor: 'pointer' }}
                        >
                          <input
                            type="radio"
                            name="overpayment"
                            checked={overpaymentAction === opt.value}
                            onChange={() => setOverpaymentAction(opt.value)}
                            disabled={loading}
                            style={{ marginTop: 2 }}
                          />
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>
                              {opt.title}
                            </div>
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                              {opt.desc}
                            </div>
                          </div>
                        </label>
                      ))}

                        {(overpaymentAction === 'refund' || leftoverAfterApply > 0) && (
                          <input
                            type="text"
                            placeholder="Refund note (how / when it will be returned)"
                            value={refundNote}
                            onChange={(e) => setRefundNote(e.target.value)}
                            disabled={loading}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              borderRadius: 10,
                              border: '1px solid #e2e8f0',
                              fontSize: 13,
                            }}
                          />
                        )}
                    </div>
                  </div>
                )}

                {/* One-time settlement — only when the amount is a genuine shortfall */}
                {canSettle && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: '10px 14px',
                      borderRadius: 10,
                      background: isSettlement ? '#ecfdf5' : 'rgba(248,250,252,0.9)',
                      border: `1px solid ${isSettlement ? '#a7f3d0' : 'rgba(226,232,240,0.8)'}`,
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={isSettlement}
                        onChange={(e) => setIsSettlement(e.target.checked)}
                        disabled={loading}
                      />
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#047857', lineHeight: 1.4 }}>
                        Accept {fmtMoney(parsedPaid)} as full &amp; final settlement — waives{' '}
                        {fmtMoney(grandTotal - parsedPaid)} and marks the month paid
                      </span>
                    </label>
                    {isSettlement && (
                      <input
                        type="text"
                        placeholder="Settlement reason / agreement note"
                        value={settlementReason}
                        onChange={(e) => setSettlementReason(e.target.value)}
                        disabled={loading}
                        style={{
                          width: '100%',
                          marginTop: 10,
                          padding: '10px 12px',
                          borderRadius: 10,
                          border: '1px solid #e2e8f0',
                          fontSize: 13,
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>

          <div style={{ textAlign: 'center' }}>
            <GlobalButton
              label={submitLabel}
              onClick={handleSubmit}
              variant="primary"
              size="medium"
              disabled={
                loading ||
                fetching ||
                !sessionId ||
                !studentId ||
                months.length === 0 ||
                (overpaid > 0 && (!overpaymentAction || applyLoading))
              }
              loading={loading}
              fullWidth={false}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default MarkFeeModal;